
import { useState, useEffect } from 'react';
import { Exercise } from '@/models/LearnTypes';

interface UseExerciseProgressProps {
  exercises: Exercise[];
  currentExerciseIndex: number;
  totalOriginalExercises: number;
  wordsToReview?: string[];
  currentExerciseId?: string;
}

interface UseExerciseProgressReturn {
  isLastExercise: boolean;
  progress: number;
  totalExercises: number;
  displayedExerciseNumber: number;
  isRecycledWord: boolean;
}

export const useExerciseProgress = ({
  exercises,
  currentExerciseIndex,
  totalOriginalExercises,
  wordsToReview = [],
  currentExerciseId
}: UseExerciseProgressProps): UseExerciseProgressReturn => {
  // Total exercises is the number of unique words
  const totalExercises = totalOriginalExercises > 0 ? totalOriginalExercises : exercises.length;
  
  // Track whether the current exercise is a tracked failed word
  const [isRecycledWord, setIsRecycledWord] = useState(false);
  
  // Determine if the current word is a tracked failed word
  useEffect(() => {
    if (!currentExerciseId) {
      setIsRecycledWord(false);
      return;
    }
    
    // Check if this word is in the wordsToReview array
    const isInReviewList = wordsToReview.includes(currentExerciseId);
    
    // Check global flag
    const globalFlag = (window as any).__HAD_RECYCLED_WORDS__ === true;
    
    setIsRecycledWord(isInReviewList || globalFlag);
  }, [currentExerciseId, wordsToReview]);
  
  // Calculate the displayed exercise number
  let displayedExerciseNumber = currentExerciseIndex + 1;
  
  // Adjust displayed number for tracked failed words
  if (isRecycledWord && totalOriginalExercises > 0) {
    displayedExerciseNumber = Math.min(totalOriginalExercises, displayedExerciseNumber);
  }
  
  // Check if this is the last exercise
  const isLastExercise = currentExerciseIndex === exercises.length - 1 || 
    (totalOriginalExercises > 0 && currentExerciseIndex === totalOriginalExercises - 1 && wordsToReview.length === 0);
  
  // Calculate progress percentage
  const progress = calculateProgress(
    isRecycledWord ? Math.min(totalOriginalExercises - 1, currentExerciseIndex) : currentExerciseIndex, 
    totalExercises
  );
  
  return {
    isLastExercise,
    progress,
    totalExercises,
    displayedExerciseNumber,
    isRecycledWord
  };
};

// Helper function to calculate progress percentage
function calculateProgress(
  currentIndex: number,
  totalExercises: number
): number {
  if (totalExercises === 0) return 0;
  return Math.min(100, ((currentIndex + 1) / totalExercises) * 100);
}
